import { useTranslation } from "react-i18next";
import RedirectingComponent from "src/chamber/components/RedirectingComponent";
import { Box } from "@mui/system";
import { useGetComplaintStatuses } from "src/complaint/data-access/useGetComplaintStatuses";

type filters = {
  chamberUsers: { label: string; value: number; isChamberAdmin: boolean }[];
  violationTypes: { label: string; value: number }[];
  isOrgAdmin: boolean;
  isSampleInitiator: boolean;
  complaintStatuses: {
    label: string;
    value: number;
  }[];
};
type badgeCountsComplaintRedirector = {
  activeComplaintApplicationsCount: number | null;
  pendingComplaintApplicationsCount: number | null;
  countsLoading?: boolean;
};
type ViolationCasesRedirectorProps = filters & badgeCountsComplaintRedirector;

function ComplaintRedirector(props: ViolationCasesRedirectorProps) {
  const { t } = useTranslation();
  const { data: allStatusesData } = useGetComplaintStatuses(
    props.isSampleInitiator,
    false,
  );
  const allFilters = [
    {
      filterName: "violationTypeId",
      filterLabel: t("Είδος παράβασης"),
      filterOptions: props?.violationTypes?.map((vt) => ({
        label: vt?.label,
        value: vt?.value,
      })),
    },
    {
      filterName: "chamberAdmin",
      filterLabel: t("Υπεύθυνος προϊστάμενος"),
      filterOptions: props.chamberUsers
        .filter((user) => user.isChamberAdmin)
        .map((user) => ({
          label: user.label,
          value: user.value,
        })),
    },
    {
      filterName: "chamberUser",
      filterLabel: t("Υπεύθυνος διεκπεραίωσης"),
      filterOptions: props.chamberUsers,
    },
    {
      filterName: "status",
      filterLabel: t("Κατάσταση"),
      filterOptions: props.complaintStatuses,
    },
  ];

  const redirectors = [
    ...(props.isSampleInitiator
      ? [
          {
            linkText: t("Καταγγελίες (όλων των Υ.Γ.Ε.ΜΗ.)"),
            linkTo: "/complaint/adminComplaintApplications",
            badgeCount: null,
            filters: [
              ...allFilters.filter((filter) =>
                ["violationTypeId"].includes(filter.filterName),
              ),
              {
                filterName: "statusId",
                filterLabel: t("Κατάσταση"),
                filterOptions:
                  allStatusesData?.data?.map((s) => ({
                    value: s.id,
                    label: s.label,
                  })) ?? [],
              },
            ],
          },
        ]
      : []),
    {
      linkText: props.isOrgAdmin
        ? t("Υποθέσεις σε εξέλιξη (δικές μου ή γενικά στην Υ. Γ.Ε.ΜΗ.)")
        : t("Υποθέσεις σε εξέλιξη (δικές μου)"),
      linkTo: "/complaint/activeComplaintApplications",
      badgeCount: props.activeComplaintApplicationsCount,
      badgeLoading: props.countsLoading,
      filters: allFilters.filter((filter) => {
        const baseFilters = ["violationTypeId", "chamberAdmin"];
        if (props.isOrgAdmin) {
          baseFilters.push("chamberUser");
        }
        return baseFilters.includes(filter?.filterName);
      }),
    },
    {
      linkText: t("Μη ανατεθειμένες υποθέσεις καταγγελιών"),
      linkTo: "/complaint/pendingComplaintApplications",
      badgeCount: props.pendingComplaintApplicationsCount,
      badgeLoading: props.countsLoading,
      filters: allFilters.filter((filter) => {
        const baseFilters = ["violationTypeId", "chamberAdmin"];
        if (props.isOrgAdmin) {
          baseFilters.push("chamberUser");
        }
        return baseFilters.includes(filter?.filterName);
      }),
    },
    {
      linkText: t("Ολοκληρωμένες υποθέσεις καταγγελιών"),
      linkTo: "/complaint/pastComplaintApplications",
      badgeCount: null,
      filters: [],
    },
  ];

  return (
    <Box
      sx={{
        padding: "1rem",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {redirectors.map((redirector) => (
        <RedirectingComponent {...redirector} />
      ))}
    </Box>
  );
}

export default ComplaintRedirector;
